import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { customerProgression, engineCapabilities, expansionNote, expansionStatement, platformProducts, talabScopeNote } from '../../data/deckSlide13';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3 | 4;
const COLUMN_X = [112, 604, 1096];
const COLUMN_W = 392;
const RAIL_Y = 374;
const COLUMN_TOP = 412;
export function PlatformExpansionSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(700, 1); // engine
    at(1700, 2); // the three products
    at(3800, 3); // Talab marketplace expansion
    at(5200, 4); // progression + statement
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2464" data-mp="2llc0tkt3wb2o">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2465" data-mp="2lksvd3kn7f5j">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 65% at 50% -22%, rgba(0,200,83,0.07) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2466" data-mp="2gm7vj59ayxhm" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2467" data-mp="34dd6p74at1ll">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2468" data-mp="2vdzwgc8co8zt" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2469" data-mp="1nzeq49kyxa1a">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2470" data-mp="1ldnpa5mkhojp">
          14 · Platform Expansion
        </motion.span>

        {/* Headline */}
        <motion.h1 className="absolute text-[40px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 128,
        width: 900
      }} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.15,
        ease
      }} data-id="element-2471" data-mp="1ggnmc3l0l1wt">
          One AI engine. Multiple products.
        </motion.h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 190,
        width: 820
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.6,
        ease
      }} data-id="element-2472" data-mp="xocqgtos2487">
          We built the underlying AI communication and conversation stack once —
          then commercialize it across individuals, businesses and commerce.
        </motion.p>

        {/* The engine */}
        <motion.div className="absolute text-center" style={{
        left: 500,
        top: 268,
        width: 600
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(1) ? 1 : 0,
        y: shown(1) ? 0 : 10
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2473" data-mp="e3kt86sj4htq">
          <div className="inline-flex items-center gap-2.5" data-id="element-2474" data-mp="j0ur8atzi1t6">
            <img src={VEXU_LOGO} alt="" className="h-5 w-5 object-contain" data-id="element-2475" data-mp="wtamuqkk2xk2" />
            <span className="text-[15px] font-semibold uppercase tracking-[0.2em] text-[#0B1712]" data-id="element-2476" data-mp="sr5vjyhkyd0l">
              VEXU AI Engine
            </span>
          </div>
          <p className="mt-2.5 text-[12px] tracking-[0.04em] text-[#8A998F]" data-id="element-2477" data-mp="16rglmzb2fnkp">
            {engineCapabilities}
          </p>
        </motion.div>

        {/* Branching rail */}
        <motion.div className="absolute w-px" style={{
        left: 800,
        top: 338,
        height: 36,
        backgroundColor: 'rgba(0,200,83,0.4)'
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(2) ? 1 : 0
      }} transition={{
        duration: 0.6,
        ease
      }} data-id="element-2478" data-mp="1lsx94811mfhq" />
        <motion.div className="absolute h-px origin-center" style={{
        left: COLUMN_X[0] + COLUMN_W / 2,
        top: RAIL_Y,
        width: COLUMN_X[2] + COLUMN_W / 2 - (COLUMN_X[0] + COLUMN_W / 2),
        backgroundColor: 'rgba(0,200,83,0.4)'
      }} initial={{
        scaleX: 0
      }} animate={{
        scaleX: shown(2) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2479" data-mp="1zn7r86ijbbet" />

        {/* Products */}
        {platformProducts.map((product, i) => {
        const isNext = product.status === 'next';
        return <div key={product.name} data-id="element-2480" data-mp="2sc56s1vd2u3v" data-mpi={i}>
              <motion.div className="absolute w-px" style={{
            left: COLUMN_X[i] + COLUMN_W / 2,
            top: RAIL_Y,
            height: COLUMN_TOP - RAIL_Y - 12,
            backgroundColor: 'rgba(0,200,83,0.4)'
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: shown(2) ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.3 + i * 0.12,
            ease
          }} data-id="element-2481" data-mp="gn8hhasadf42" />

              <motion.div className="absolute" style={{
            left: COLUMN_X[i],
            top: COLUMN_TOP,
            width: COLUMN_W
          }} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: shown(2) ? 1 : 0,
            y: shown(2) ? 0 : 10
          }} transition={{
            duration: 0.8,
            delay: 0.35 + i * 0.18,
            ease
          }} data-id="element-2482" data-mp="3p2s9k6h80rhj">
                <div className="flex items-baseline gap-3" data-id="element-2483" data-mp="1de9d682q8qkq">
                  <span className="text-[11px] font-semibold tracking-[0.24em] text-vexu-700/60 tabular-nums" data-id="element-2484" data-mp="37tg4g5a7l0qg">
                    {product.index}
                  </span>
                  <span className="text-[20px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2485" data-mp="3clzggxt8znt5">
                    {product.name}
                  </span>
                  <span className={`rounded-full px-2 py-[3px] text-[9.5px] font-semibold uppercase tracking-[0.18em] ${isNext ? 'bg-vexu/[0.09] text-vexu-700' : 'bg-[#0B1712]/[0.05] text-[#5A6B60]'}`} data-id="element-2486" data-mp="32v4vdwrqmu9a">
                    {isNext ? 'Demo ready' : 'Live'}
                  </span>
                </div>
                <p className="mt-2 text-[12.5px] text-[#7C8B82]" data-id="element-2487" data-mp="3hl2h862737vb">
                  {product.audience}
                </p>
                {isNext && <p className="mt-2 text-[11.5px] leading-snug text-vexu-700/80" data-id="element-2488" data-mp="262ri11r3v55y">
                    {talabScopeNote}
                  </p>}

                <div className="mt-5 space-y-2.5" data-id="element-2489" data-mp="22lwqpqeg2274">
                  {product.items.map(item => <div key={item} className="flex items-center gap-2.5" data-id="element-2490" data-mp="1xmtvsys6hccv">
                      <span className="rounded-full" style={{
                  width: 4,
                  height: 4,
                  backgroundColor: 'rgba(0,153,64,0.4)'
                }} data-id="element-2491" data-mp="1xowlfhpf53h2" />
                      <span className="text-[13.5px] leading-tight text-[#33463B]" data-id="element-2492" data-mp="1jtb3fuhlrjy5">
                        {item}
                      </span>
                    </div>)}
                </div>

                {product.highlight && <motion.div className="mt-6" initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: shown(3) ? 1 : 0,
              y: shown(3) ? 0 : 6
            }} transition={{
              duration: 0.8,
              ease
            }} data-id="element-2493" data-mp="1otgxfhbchije">
                    <span className="inline-flex items-center rounded-full border border-vexu/25 bg-vexu/[0.07] px-3 py-1.5 text-[12px] font-semibold text-vexu-700" data-id="element-2494" data-mp="aomapgb12pkx">
                      {product.highlight.label}
                    </span>
                    <p className="mt-3 text-[11.5px] leading-snug text-[#A3AFA7]" data-id="element-2495" data-mp="omsopbwgnuto">
                      {product.highlight.note}
                    </p>
                  </motion.div>}
              </motion.div>
            </div>;
      })}

        {/* Bottom — the progression across the stack */}
        <motion.div className="absolute border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        bottom: 44
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(4) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2496" data-mp="13c8fgrvzs09d">
          <div className="flex items-center gap-3" data-id="element-2497" data-mp="1ha6lna05sqds">
            {customerProgression.map((step, i) => <React.Fragment key={step} data-id="element-2498" data-mp="31u1zqtbvs1dv" data-mpi={i}>
                {i > 0 && <span className="text-[11px] text-[#C3CCC5]" data-id="element-2499" data-mp="2wtw5r6i522sm">→</span>}
                <span className="text-[12.5px] uppercase tracking-[0.16em] text-[#7C8B82]" data-id="element-2500" data-mp="2wsl1z6op0v1i">
                  {step}
                </span>
              </React.Fragment>)}
          </div>
          <p className="mt-4 text-[21px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2501" data-mp="2nv2d1r7xhwkc">
            {expansionStatement}
          </p>
          <p className="mt-2 text-[13px] text-[#7C8B82]" data-id="element-2502" data-mp="3gi58ubl7zt6w">{expansionNote}</p>
        </motion.div>
      </div>
    </SlideStage>;
}